/**
 * Hooks for admin area CRUD + listing.
 * Cover images resolved via media_assets (owner_type='area', kind='cover') — canonical SoT.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { resolveAssetUrl } from "@/lib/media/mediaApi";
import type { MediaAsset } from "@/lib/media/mediaTypes";

export interface AdminAreaRow {
  id: string;
  slug: string;
  name: string;
  area_type: string;
  status: string;
  description_short: string | null;
  description_long: string | null;
  municipality: string | null;
  county: string | null;
  landskap: string | null;
  lat: number | null;
  lng: number | null;
  parent_area_id: string | null;
  hero_media_asset_id: string | null;
  updated_at: string;
  created_at: string;
  linked_places_count?: number;
  hero_url?: string | null;
}

const AREA_TYPES = ["bygd", "destination", "landscape_cluster", "region"] as const;
export type AreaType = (typeof AREA_TYPES)[number];
export { AREA_TYPES };

// ── List all areas (admin) ──

export function useAdminAreasList() {
  return useQuery<AdminAreaRow[]>({
    queryKey: ["admin-areas"],
    queryFn: async () => {
      // Fetch areas
      const { data: areas, error } = await supabase
        .from("areas")
        .select("*")
        .order("name");

      if (error) throw error;

      // Fetch linked place counts
      const { data: counts, error: countError } = await supabase
        .from("place_areas")
        .select("area_id");

      if (countError) console.warn("[useAdminAreasList] count error", countError);

      const countMap = new Map<string, number>();
      (counts || []).forEach((row) => {
        countMap.set(row.area_id, (countMap.get(row.area_id) || 0) + 1);
      });

      // Resolve cover images via media_assets (canonical SoT)
      const areaIds = areas.map((a) => a.id);
      let heroMap = new Map<string, string>();
      if (areaIds.length > 0) {
        const { data: coverAssets } = await supabase
          .from("media_assets")
          .select("*")
          .eq("owner_type", "area")
          .eq("kind", "cover")
          .in("owner_id", areaIds)
          .order("sort_order", { ascending: true });

        for (const row of (coverAssets ?? []) as unknown as MediaAsset[]) {
          if (row.owner_id && !heroMap.has(row.owner_id)) {
            const url = resolveAssetUrl(row, 320);
            if (url) heroMap.set(row.owner_id, url);
          }
        }
      }

      return areas.map((a) => ({
        ...a,
        linked_places_count: countMap.get(a.id) || 0,
        hero_url: heroMap.get(a.id) || null,
      }));
    },
  });
}

// ── Single area ──

export function useAdminArea(areaId: string | undefined) {
  return useQuery({
    queryKey: ["admin-area", areaId],
    enabled: Boolean(areaId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("areas")
        .select("*")
        .eq("id", areaId!)
        .single();
      if (error) throw error;
      return data;
    },
  });
}

// ── Create ──

export function useCreateArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: {
        name: string;
        slug: string;
        area_type: string;
        status?: string;
        description_short?: string | null;
        description_long?: string | null;
        municipality?: string | null;
        county?: string | null;
        landskap?: string | null;
        lat?: number | null;
        lng?: number | null;
        parent_area_id?: string | null;
        hero_media_asset_id?: string | null;
      }
    ) => {
      const { data, error } = await supabase
        .from("areas")
        .insert({ ...payload, status: payload.status || "draft" })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-areas"] });
      toast.success("Område skapat");
    },
    onError: (e: any) => {
      toast.error(`Kunde inte skapa område: ${e.message}`);
    },
  });
}

// ── Update ──

export function useUpdateArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Record<string, any>;
    }) => {
      const { error } = await supabase
        .from("areas")
        .update(updates)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["admin-areas"] });
      qc.invalidateQueries({ queryKey: ["admin-area", vars.id] });
      toast.success("Område uppdaterat");
    },
    onError: (e: any) => {
      toast.error(`Kunde inte uppdatera: ${e.message}`);
    },
  });
}

// ── Area linked places ──

export interface AreaLinkedPlace {
  place_id: string;
  is_primary: boolean;
  sort_order: number;
  place_name: string;
  place_slug: string;
  place_status: string;
  place_municipality: string | null;
}

export function useAreaLinkedPlaces(areaId: string | undefined) {
  return useQuery<AreaLinkedPlace[]>({
    queryKey: ["area-linked-places", areaId],
    enabled: Boolean(areaId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("place_areas")
        .select("place_id, is_primary, sort_order, places!inner(name, slug, status, municipality)")
        .eq("area_id", areaId!)
        .order("sort_order");

      if (error) throw error;

      return (data || []).map((row) => {
        const place = row.places as unknown as {
          name: string;
          slug: string;
          status: string;
          municipality: string | null;
        };
        return {
          place_id: row.place_id,
          is_primary: row.is_primary,
          sort_order: row.sort_order,
          place_name: place.name,
          place_slug: place.slug,
          place_status: place.status,
          place_municipality: place.municipality,
        };
      });
    },
  });
}

// ── Place's areas (for Sammanhang card) ──

export interface PlaceAreaLink {
  area_id: string;
  is_primary: boolean;
  area_name: string;
  area_slug: string;
  area_type: string;
}

export function usePlaceAreas(placeId: string | undefined) {
  return useQuery<PlaceAreaLink[]>({
    queryKey: ["place-areas", placeId],
    enabled: Boolean(placeId),
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("place_areas")
        .select("area_id, is_primary, areas!inner(name, slug, area_type, status)")
        .eq("place_id", placeId!);

      if (error) throw error;

      return (data || []).map((row) => {
        const area = row.areas as unknown as {
          name: string;
          slug: string;
          area_type: string;
          status: string;
        };
        return {
          area_id: row.area_id,
          is_primary: row.is_primary,
          area_name: area.name,
          area_slug: area.slug,
          area_type: area.area_type,
        };
      });
    },
  });
}

// ── Link / Unlink place ↔ area ──

export function useLinkPlaceArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      placeId,
      areaId,
      isPrimary = false,
    }: {
      placeId: string;
      areaId: string;
      isPrimary?: boolean;
    }) => {
      const { error } = await supabase
        .from("place_areas")
        .insert({ place_id: placeId, area_id: areaId, is_primary: isPrimary });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["area-linked-places"] });
      qc.invalidateQueries({ queryKey: ["place-areas"] });
      qc.invalidateQueries({ queryKey: ["admin-areas"] });
      toast.success("Plats kopplad till område");
    },
    onError: (e: any) => {
      toast.error(`Kunde inte koppla: ${e.message}`);
    },
  });
}

export function useUnlinkPlaceArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      placeId,
      areaId,
    }: {
      placeId: string;
      areaId: string;
    }) => {
      const { error } = await supabase
        .from("place_areas")
        .delete()
        .eq("place_id", placeId)
        .eq("area_id", areaId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["area-linked-places"] });
      qc.invalidateQueries({ queryKey: ["place-areas"] });
      qc.invalidateQueries({ queryKey: ["admin-areas"] });
      toast.success("Koppling borttagen");
    },
    onError: (e: any) => {
      toast.error(`Kunde inte ta bort koppling: ${e.message}`);
    },
  });
}

// ── Set primary area for a place (atomic: clears others first) ──

export function useSetPrimaryArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      placeId,
      areaId,
    }: {
      placeId: string;
      areaId: string;
    }) => {
      // Clear all existing primary flags for this place
      const { error: clearError } = await supabase
        .from("place_areas")
        .update({ is_primary: false })
        .eq("place_id", placeId)
        .eq("is_primary", true);
      if (clearError) throw clearError;

      // Set the target relation as primary
      const { error: setError } = await supabase
        .from("place_areas")
        .update({ is_primary: true })
        .eq("place_id", placeId)
        .eq("area_id", areaId);
      if (setError) throw setError;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["area-linked-places"] });
      qc.invalidateQueries({ queryKey: ["place-areas"] });
      toast.success("Primärt område uppdaterat");
    },
    onError: (e: any) => {
      toast.error(`Kunde inte sätta primärt: ${e.message}`);
    },
  });
}

// ── Unset primary area for a place ──

export function useUnsetPrimaryArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      placeId,
      areaId,
    }: {
      placeId: string;
      areaId: string;
    }) => {
      const { error } = await supabase
        .from("place_areas")
        .update({ is_primary: false })
        .eq("place_id", placeId)
        .eq("area_id", areaId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["area-linked-places"] });
      qc.invalidateQueries({ queryKey: ["place-areas"] });
      toast.success("Primärt område borttaget");
    },
    onError: (e: any) => {
      toast.error(`Kunde inte ta bort primärt: ${e.message}`);
    },
  });
}

// ── Reorder linked places for an area ──

export function useReorderAreaPlaces() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      areaId,
      orderedPlaceIds,
    }: {
      areaId: string;
      orderedPlaceIds: string[];
    }) => {
      // Update sort_order for each place in the new order
      const updates = orderedPlaceIds.map((placeId, index) =>
        supabase
          .from("place_areas")
          .update({ sort_order: index })
          .eq("area_id", areaId)
          .eq("place_id", placeId)
      );
      const results = await Promise.all(updates);
      const failed = results.find((r) => r.error);
      if (failed?.error) throw failed.error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["area-linked-places"] });
    },
    onError: (e: any) => {
      qc.invalidateQueries({ queryKey: ["area-linked-places"] });
      toast.error(`Kunde inte spara ordning: ${e.message}`);
    },
  });
}
